import { useEffect, useMemo, useState, type ComponentProps } from 'react'
import { Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { ArrowLeft, ArrowRight, Check, CheckCircle2, ExternalLink, Maximize2, RotateCcw, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { BlockItem } from './parse'
import { firstOpenStep, guideKey, readDone } from './guideProgress'

/**
 * Step bodies are block markdown; internal links stay in the app. A table is
 * the "what to enter" list (Field | What to enter | Example), its first column
 * naming the field as it reads on screen.
 */
export function StepMd({ children }: { children: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        table: ({ children: c }: ComponentProps<'table'>) => (
          <div className='my-3 overflow-x-persistent rounded-lg border'>
            <table className='!my-0 w-full border-collapse text-[13px]'>{c}</table>
          </div>
        ),
        thead: ({ children: c }: ComponentProps<'thead'>) => <thead className='bg-muted/60'>{c}</thead>,
        th: ({ children: c }: ComponentProps<'th'>) => (
          <th className='border-b px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>{c}</th>
        ),
        td: ({ children: c }: ComponentProps<'td'>) => (
          <td className='border-b px-3 py-2 align-top text-muted-foreground first:font-medium first:text-foreground [tr:last-child_&]:border-b-0'>{c}</td>
        ),
        p: ({ children: c }: ComponentProps<'p'>) => <div className='mb-2 last:mb-0'>{c}</div>,
        ul: ({ children: c }: ComponentProps<'ul'>) => <div className='mb-2 space-y-1 last:mb-0'>{c}</div>,
        ol: ({ children: c }: ComponentProps<'ol'>) => <div className='mb-2 space-y-1 last:mb-0'>{c}</div>,
        li: ({ children: c }: ComponentProps<'li'>) => (
          <div className='flex gap-2'>
            <span className='mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60' aria-hidden='true' />
            <div className='min-w-0'>{c}</div>
          </div>
        ),
        a: ({ href, children: c }: ComponentProps<'a'>) =>
          href?.startsWith('/')
            ? <Link to={href}>{c}</Link>
            // `./page` is another wiki page, resolved against the current URL.
            : href?.startsWith('.')
              ? <a href={href}>{c}</a>
              : <a href={href} target='_blank' rel='noopener noreferrer'>{c}</a>,
      }}
    >
      {children}
    </ReactMarkdown>
  )
}

function Screenshot({ src, alt }: { src: string; alt: string }) {
  const [zoomed, setZoomed] = useState(false)
  useEffect(() => {
    if (!zoomed) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setZoomed(false)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [zoomed])
  return (
    <>
      <button
        type='button'
        onClick={() => setZoomed(true)}
        className='group relative mt-4 block w-full overflow-hidden rounded-lg border bg-muted/30 text-left'
        aria-label={`Enlarge screenshot: ${alt}`}
      >
        <img src={src} alt={alt} loading='lazy' className='!m-0 block w-full' />
        <span className='absolute right-2 top-2 flex items-center gap-1 rounded-md bg-background/90 px-2 py-1 text-[11px] font-medium text-muted-foreground opacity-0 shadow-sm transition-opacity group-hover:opacity-100'>
          <Maximize2 className='h-3 w-3' /> Enlarge
        </span>
      </button>
      {zoomed && (
        <div
          role='dialog'
          aria-modal='true'
          aria-label={alt}
          className='fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6'
          onClick={() => setZoomed(false)}
        >
          <button type='button' className='absolute right-4 top-4 rounded-full bg-background p-2 shadow' aria-label='Close'>
            <X className='h-4 w-4' />
          </button>
          <img src={src} alt={alt} className='max-h-full max-w-full rounded-lg shadow-2xl' />
        </div>
      )}
    </>
  )
}

/**
 * A ```guide fence: a walkthrough shown one step at a time. Each step can carry
 * the screen it happens on (`meta:`), a button straight to it (`link:`), a
 * screenshot (`image:`) and what to confirm before moving on (`check:`).
 * Ticked steps are kept in localStorage (see guideProgress.ts).
 */
export function Guide({ title, items, label, onProgress }: {
  /** Names the guide and keys its saved progress. */
  title: string
  items: BlockItem[]
  /** Shown in the header instead of the title (a ```setup question shows its own). */
  label?: string
  onProgress?: (done: number) => void
}) {
  const titles = useMemo(() => items.map(i => i.title), [items])
  const key = guideKey(title || titles[0] || '')
  const [done, setDone] = useState<Set<string>>(() => readDone(localStorage.getItem(key), titles))
  const [current, setCurrent] = useState(() => firstOpenStep(titles, done))

  useEffect(() => {
    localStorage.setItem(key, JSON.stringify([...done]))
    onProgress?.(done.size)
  }, [key, done, onProgress])

  if (items.length === 0) return null
  const step = items[current]
  const isDone = done.has(step.title)
  const isLast = current === items.length - 1
  const pct = Math.round((done.size / items.length) * 100)

  const toggle = (t: string, value: boolean) =>
    setDone(prev => {
      const next = new Set(prev)
      if (value) next.add(t)
      else next.delete(t)
      return next
    })
  const markDoneAndNext = () => {
    toggle(step.title, true)
    if (!isLast) setCurrent(current + 1)
  }

  return (
    <section className='help-guide not-prose my-6 overflow-hidden rounded-2xl border bg-card shadow-sm' aria-label={title || 'Walkthrough'}>
      <header className='flex flex-wrap items-center gap-3 border-b bg-muted/30 px-5 py-3'>
        <div className='min-w-0 flex-1'>
          <div className='text-sm font-semibold text-foreground'>{label ?? (title || 'Walkthrough')}</div>
          <div className='text-xs text-muted-foreground'>
            {done.size === items.length ? 'All steps done' : `${done.size} of ${items.length} steps done`}
          </div>
        </div>
        <div className='h-2 w-24 overflow-hidden rounded-full bg-muted sm:w-40' role='progressbar' aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
          <div className='h-full rounded-full bg-emerald-500 transition-all' style={{ width: `${pct}%` }} />
        </div>
        {done.size > 0 && (
          <button
            type='button'
            onClick={() => { setDone(new Set()); setCurrent(0) }}
            className='flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground'
          >
            <RotateCcw className='h-3 w-3' /> Start over
          </button>
        )}
      </header>

      <div className='grid grid-cols-[minmax(0,1fr)] md:grid-cols-[240px_minmax(0,1fr)]'>
        <nav className='min-w-0 border-b p-2 md:border-b-0 md:border-r' aria-label='Steps'>
          <div className='flex gap-1 overflow-x-auto md:max-h-[680px] md:flex-col md:overflow-y-auto'>
            {items.map((item, i) => {
              const ticked = done.has(item.title)
              return (
                <button
                  key={item.title}
                  type='button'
                  onClick={() => setCurrent(i)}
                  aria-current={i === current ? 'step' : undefined}
                  className={cn(
                    'flex shrink-0 items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[13px] transition-colors',
                    i === current ? 'bg-primary/10 font-medium text-foreground' : 'text-muted-foreground hover:bg-muted',
                  )}
                >
                  <span
                    className={cn(
                      'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold',
                      ticked ? 'border-emerald-500 bg-emerald-500 text-white' : i === current ? 'border-primary text-primary' : 'border-border',
                    )}
                  >
                    {ticked ? <Check className='h-3.5 w-3.5' /> : i + 1}
                  </span>
                  <span className='leading-snug md:line-clamp-2'>{item.title}</span>
                </button>
              )
            })}
          </div>
        </nav>

        <div className='min-w-0 p-5'>
          <div className='flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>
            Step {current + 1} of {items.length}
            {step.attrs.badge && <span className='rounded-full bg-amber-500/15 px-2 py-0.5 text-amber-700 dark:text-amber-400'>{step.attrs.badge}</span>}
            {isDone && <span className='flex items-center gap-1 text-emerald-600'><CheckCircle2 className='h-3.5 w-3.5' /> Done</span>}
          </div>
          <h3 className='!mb-1 !mt-1 text-lg font-semibold text-foreground'>{step.title}</h3>
          {step.attrs.meta && (
            <div className='mb-3 inline-block rounded-md bg-muted px-2 py-0.5 font-mono text-[12px] text-muted-foreground'>
              {step.attrs.meta}
            </div>
          )}

          <div className='text-[14px] leading-relaxed text-foreground/90'>
            <StepMd>{step.body}</StepMd>
          </div>

          {step.attrs.image && <Screenshot src={step.attrs.image} alt={step.title} />}

          {step.attrs.check && (
            <div className='mt-4 flex gap-2.5 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-[13px]'>
              <CheckCircle2 className='mt-0.5 h-4 w-4 shrink-0 text-emerald-600' aria-hidden='true' />
              <div>
                <span className='font-semibold text-foreground'>Check before you continue: </span>
                <span className='text-muted-foreground'>{step.attrs.check}</span>
              </div>
            </div>
          )}

          <div className='mt-5 flex flex-wrap items-center gap-2 border-t pt-4'>
            <button
              type='button'
              onClick={() => setCurrent(current - 1)}
              disabled={current === 0}
              className='flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm disabled:opacity-40'
            >
              <ArrowLeft className='h-4 w-4' /> Back
            </button>
            {step.attrs.link && (
              <Link
                to={step.attrs.link}
                target='_blank'
                className='flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm text-primary !no-underline hover:bg-primary/5'
              >
                Open this screen <ExternalLink className='h-3.5 w-3.5' />
              </Link>
            )}
            <div className='flex-1' />
            {isDone ? (
              <>
                <button type='button' onClick={() => toggle(step.title, false)} className='px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground'>
                  Mark not done
                </button>
                {!isLast && (
                  <button type='button' onClick={() => setCurrent(current + 1)} className='flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground'>
                    Next <ArrowRight className='h-4 w-4' />
                  </button>
                )}
              </>
            ) : (
              <button type='button' onClick={markDoneAndNext} className='flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700'>
                <Check className='h-4 w-4' /> {isLast ? 'Mark done' : 'Done — next step'}
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
